import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import ImportExportBar from "./ImportExportBar";

const API = "/api/v1/salesman-beats";

const salesmanLabel = (s) => `${s.userName || `Salesman ${s.id}`}${s.userPhone ? ` (${s.userPhone})` : ""}`;
const beatLabel = (b) => `${b.beatName}${b.beatCode ? ` (${b.beatCode})` : ""}`;

/**
 * Which salesman covers which beat (one salesman per beat, a salesman can have many beats). Assign beats to a salesman, edit or
 * delete an assignment, or import a sheet with Salesman Name and Beat Name columns.
 */
const SalesmanBeats = () => {
  const [rows, setRows] = useState([]);
  const [beats, setBeats] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSalesman, setFilterSalesman] = useState("");
  const [search, setSearch] = useState("");

  // create: salesman + several beats; edit: one row's beat and salesman
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ salesPersonId: "", beatIds: new Set() });
  const [beatSearch, setBeatSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ beatId: "", salesPersonId: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(API);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load salesman beats", error);
      toast.error(error.message || "Failed to load salesman beats");
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [beatData, salesmanData] = await Promise.all([
        api.get("/api/v1/beats", { size: 1000 }),
        api.get("/api/v1/sales-persons", { type: "SALESMAN" }),
      ]);
      setBeats(beatData?.content || []);
      setSalesmen(Array.isArray(salesmanData) ? salesmanData : []);
    } catch (error) {
      console.error("Failed to load beats / salesmen", error);
      toast.error(error.message || "Failed to load beats / salesmen");
    }
  };

  useEffect(() => {
    load();
    loadLookups();
  }, []);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!filterSalesman || String(row.salesPersonId) === filterSalesman) &&
        (!term || `${row.salesmanName || ""} ${row.beatName} ${row.beatCode || ""}`.toLowerCase().includes(term))
    );
  }, [rows, filterSalesman, search]);

  const openCreate = () => {
    setCreateForm({ salesPersonId: filterSalesman || "", beatIds: new Set() });
    setBeatSearch("");
    setCreating(true);
  };

  const toggleBeat = (id) =>
    setCreateForm((prev) => {
      const next = new Set(prev.beatIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, beatIds: next };
    });

  const salesmanOfBeat = useMemo(() => new Map(rows.map((row) => [row.beatId, row])), [rows]);

  const handleCreate = async () => {
    if (!createForm.salesPersonId) {
      toast.error("Select a salesman");
      return;
    }
    if (createForm.beatIds.size === 0) {
      toast.error("Select at least one beat");
      return;
    }
    setSaving(true);
    try {
      await api.post(API, { salesPersonId: Number(createForm.salesPersonId), beatIds: Array.from(createForm.beatIds) });
      toast.success(`${createForm.beatIds.size} beat(s) assigned`);
      setCreating(false);
      load();
    } catch (error) {
      console.error("Failed to assign beats", error);
      toast.error(error.message || "Failed to assign beats");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({ beatId: String(row.beatId), salesPersonId: String(row.salesPersonId ?? "") });
  };

  const handleUpdate = async () => {
    if (!editForm.beatId || !editForm.salesPersonId) {
      toast.error("Select a beat and a salesman");
      return;
    }
    const other = salesmanOfBeat.get(Number(editForm.beatId));
    if (
      Number(editForm.beatId) !== editing.beatId &&
      other &&
      !window.confirm(`${other.beatName} is assigned to ${other.salesmanName || "another salesman"}. Move it?`)
    )
      return;
    setSaving(true);
    try {
      await api.put(`${API}/${editing.beatId}`, { beatId: Number(editForm.beatId), salesPersonId: Number(editForm.salesPersonId) });
      toast.success("Assignment updated");
      setEditing(null);
      load();
    } catch (error) {
      console.error("Failed to update assignment", error);
      toast.error(error.message || "Failed to update assignment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ${row.salesmanName || "the salesman"} from beat ${row.beatName}?`)) return;
    try {
      await api.del(`${API}/${row.beatId}`);
      toast.success("Assignment deleted");
      load();
    } catch (error) {
      console.error("Failed to delete assignment", error);
      toast.error(error.message || "Failed to delete assignment");
    }
  };

  const pickableBeats = beats.filter((b) => !beatSearch.trim() || beatLabel(b).toLowerCase().includes(beatSearch.trim().toLowerCase()));

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading salesman beats...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + Assign Beats
        </button>
        <select value={filterSalesman} onChange={(e) => setFilterSalesman(e.target.value)} aria-label="Salesman">
          <option value="">All salesmen</option>
          {salesmen.map((s) => (
            <option key={s.id} value={s.id}>
              {salesmanLabel(s)}
            </option>
          ))}
        </select>
        <input placeholder="Search salesman / beat" value={search} onChange={(e) => setSearch(e.target.value)} />
        <ImportExportBar importPath="/api/v1/salesman-beats/import" label="Salesman Beats" onImported={load} />
      </div>
      <div className="page-subtitle" style={{ margin: "4px 0 10px" }}>
        Excel import columns: <b>Salesman Name</b>, <b>Beat Name</b>. The salesman is matched by name (or employee code) and the beat by
        name; a beat assigned to another salesman moves to this one.
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Salesman</th>
              <th>Employee Code</th>
              <th>Beat</th>
              <th>Beat Code</th>
              <th>Territory</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={7}>No salesman is assigned to a beat yet.</td>
              </tr>
            ) : (
              shown.map((row) => (
                <tr key={row.beatId}>
                  <td>{row.salesmanName || "-"}</td>
                  <td>{row.employeeCode || "-"}</td>
                  <td>{row.beatName}</td>
                  <td>{row.beatCode || "-"}</td>
                  <td>{row.territory || "-"}</td>
                  <td>
                    <span className={`badge ${row.active ? "badge-green" : "badge-gray"}`}>{row.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="action-buttons flex gap-2">
                    <button className="secondary" onClick={() => openEdit(row)}>
                      Edit
                    </button>
                    <button className="secondary" onClick={() => handleDelete(row)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setCreating(false)}>
          <div className="modal-box" style={{ maxWidth: "640px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Assign Beats to Salesman</h3>
              <button className="close-btn" onClick={() => setCreating(false)}>
                Close
              </button>
            </div>
            <div className="form-group">
              <label>Salesman</label>
              <select value={createForm.salesPersonId} onChange={(e) => setCreateForm((prev) => ({ ...prev, salesPersonId: e.target.value }))}>
                <option value="">-- Select salesman --</option>
                {salesmen.map((s) => (
                  <option key={s.id} value={s.id}>
                    {salesmanLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Beats ({createForm.beatIds.size} selected)</label>
              <input placeholder="Search beats" value={beatSearch} onChange={(e) => setBeatSearch(e.target.value)} />
            </div>
            <div style={{ maxHeight: "40vh", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
              {pickableBeats.length === 0 ? (
                <div style={{ padding: "8px 10px" }}>No beats found.</div>
              ) : (
                pickableBeats.map((beat) => {
                  const current = salesmanOfBeat.get(beat.id);
                  return (
                    <label
                      key={beat.id}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 10px", borderBottom: "1px solid #f1f1f1" }}
                    >
                      <input type="checkbox" checked={createForm.beatIds.has(beat.id)} onChange={() => toggleBeat(beat.id)} />
                      <span>
                        {beatLabel(beat)}
                        {current && String(current.salesPersonId) !== String(createForm.salesPersonId) ? (
                          <span style={{ color: "#b45309" }}> — now with {current.salesmanName || "another salesman"}, will move</span>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleCreate} disabled={saving}>
                {saving ? "Saving..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editing ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="modal-box" style={{ maxWidth: "520px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Edit Assignment</h3>
              <button className="close-btn" onClick={() => setEditing(null)}>
                Close
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Salesman</label>
                <select value={editForm.salesPersonId} onChange={(e) => setEditForm((prev) => ({ ...prev, salesPersonId: e.target.value }))}>
                  <option value="">-- Select salesman --</option>
                  {salesmen.map((s) => (
                    <option key={s.id} value={s.id}>
                      {salesmanLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Beat</label>
                <select value={editForm.beatId} onChange={(e) => setEditForm((prev) => ({ ...prev, beatId: e.target.value }))}>
                  {beats.map((b) => (
                    <option key={b.id} value={b.id}>
                      {beatLabel(b)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleUpdate} disabled={saving}>
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalesmanBeats;
